// רקע המערכת - a very faint drawing on the gray behind the cards, on every page (15/10/2026). The owner, on a
// ChatGPT mockup of /settings: "i want all the gray backgrounds to be with this subtle wavy design like in the graphs
// in the homepage", "but very subtle please", "add this setting in the הגדרות and it is off by default let the user
// choose from a few very subtle designs".
// PageBackdrop.vue paints a design, App.vue puts it under the page (.page-body turns transparent), and
// Settings/PageBackgroundSettings.vue offers them. The business's choice: homepage_setting.settings.pageBackground
// (the tenant's display blob, no migration); missing / unknown = the plain gray.
//
// A design is bands of SVG and / or glows (CSS radial gradients), in the colors of the homepage's waves:
//   top    - from just under the header down, behind the page's title (cropped at the sides, never squeezed)
//   bottom - the bottom of the screen, the same way
// A band's layers are drawn back to front. A layer runs smoothly through its points (flat at each one - a crest or a
// trough). A fill goes down to the band's base and fades from its top (opacities[0]) to the base (opacities[1]), or -
// across - runs from colors[0] on the left to colors[1] on the right. A line is the curve alone, 1.4px at any size.

import { WAVE_COLORS } from './waves';

const { sky, lavender, blue, mint } = WAVE_COLORS;

// "M x,y C ..." through the points, each segment leaving and reaching its points flat
export function smoothCurve(points) {
    let d = 'M' + points[0][0] + ',' + points[0][1];
    for (let i = 1; i < points.length; i++) {
        const x0 = points[i - 1][0], y0 = points[i - 1][1], x1 = points[i][0], y1 = points[i][1];
        const k = Math.round((x1 - x0) * 0.42);
        d += ' C' + (x0 + k) + ',' + y0 + ' ' + (x1 - k) + ',' + y1 + ' ' + x1 + ',' + y1;
    }
    return d;
}

function fill(points, colors, opacities, across) {
    return { kind: 'fill', points: points, colors: colors, opacities: opacities, across: !!across };
}

function line(points, color, opacity) {
    return { kind: 'line', points: points, color: color, opacity: opacity };
}

// one silk thread of the קווים design: crests and troughs every half period, from before the band's left edge to
// after its right one, so no end shows. The threads share the period and the phase and differ in amplitude, so
// they run together on one side and fan out on the other - a ribbon
function thread(y, amp, period, phase) {
    const points = [];
    for (let i = 0, x = phase - period; x <= 1600 + period; i++, x += period / 2)
        points.push([Math.round(x), Math.round(y + (i % 2 ? amp : -amp))]);
    return points;
}

export const PAGE_BACKDROPS = [
    {
        key: 'waves',
        label: 'גלים',
        top: {
            width: 1600, height: 260,
            layers: [
                fill([[0, 34], [380, 10], [800, 74], [1220, 22], [1600, 50]], [sky, sky], [0.055, 0]),
                fill([[0, 104], [420, 70], [860, 122], [1260, 58], [1600, 86]], [lavender, lavender], [0.045, 0]),
            ],
        },
        bottom: {
            width: 1600, height: 420,
            layers: [
                fill([[0, 96], [330, 58], [700, 132], [1070, 64], [1440, 104], [1600, 96]], [lavender, lavender], [0.05, 0]),
                fill([[0, 176], [360, 138], [760, 206], [1150, 144], [1600, 176]], [sky, sky], [0.045, 0]),
                fill([[0, 268], [260, 236], [570, 288], [900, 246], [1200, 276], [1480, 236], [1600, 250]], [mint, mint], [0.07, 0.005]),
            ],
        },
    },
    {
        key: 'dunes',
        label: 'גבעות',
        bottom: {
            width: 1600, height: 420,
            layers: [
                fill([[0, 190], [560, 96], [1180, 200], [1600, 140]], [lavender, blue], [0.06, 0.05], true),
                fill([[0, 300], [460, 236], [1040, 312], [1600, 232]], [blue, blue], [0.06, 0.015]),
            ],
        },
    },
    {
        key: 'lines',
        label: 'קווים',
        top: {
            width: 1600, height: 260,
            layers: [
                line(thread(36, 14, 1500, 200), sky, 0.22),
                line(thread(46, 22, 1500, 200), lavender, 0.2),
                line(thread(56, 30, 1500, 200), mint, 0.2),
            ],
        },
        bottom: {
            width: 1600, height: 420,
            layers: [
                line(thread(174, 18, 1500, 260), lavender, 0.22),
                line(thread(186, 27, 1500, 260), sky, 0.22),
                line(thread(198, 36, 1500, 260), mint, 0.24),
                line(thread(210, 45, 1500, 260), lavender, 0.2),
                line(thread(222, 54, 1500, 260), sky, 0.2),
                line(thread(234, 63, 1500, 260), mint, 0.22),
            ],
        },
    },
    {
        key: 'mist',
        label: 'ערפל',
        // [color, opacity at the center, ellipse size, center] - percent of the screen
        glows: [
            [lavender, 0.12, '55% 42%', '86% 12%'],
            [sky, 0.09, '45% 36%', '20% 8%'],
            [mint, 0.12, '55% 48%', '8% 100%'],
            [blue, 0.09, '50% 44%', '92% 100%'],
        ],
    },
];

export function backdropByKey(key) {
    return PAGE_BACKDROPS.find(design => design.key === key) || null;
}

function rgba(hex, alpha) {
    const n = parseInt(hex.slice(1), 16);
    return 'rgba(' + (n >> 16) + ',' + ((n >> 8) & 255) + ',' + (n & 255) + ',' + Math.round(alpha * 1000) / 1000 + ')';
}

// the glows as a CSS background ('none' when the design has none). `strength` scales every opacity (the settings'
// small previews draw a little stronger than the page, or the shapes would not read at that size)
export function backdropGlows(design, strength) {
    const s = strength || 1;
    const glows = (design && design.glows) || [];
    if (!glows.length)
        return 'none';
    return glows.map(g => 'radial-gradient(' + g[2] + ' at ' + g[3] + ', ' + rgba(g[0], g[1] * s) + ' 0%, ' +
        rgba(g[0], g[1] * s * 0.45) + ' 45%, ' + rgba(g[0], 0) + ' 100%)').join(', ');
}
